import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function EmergencyShelterTurkey() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-purple-600 via-pink-500 to-rose-500 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-white/90 hover:text-white mb-6 transition-colors">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Blog
          </Link>
          <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-1 rounded-full text-sm font-medium mb-4">
            Emergency Resources • Turkey
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            Türkiye'de Acil Barınma: Nereye Gidebilirsiniz?
          </h1>
          <p className="text-xl text-white/90 mb-6">
            Where to Go When You Need Shelter in Turkey: A Complete Guide to Emergency Housing, Support Services, and Financial Assistance
          </p>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center">
              <i className="ri-time-line mr-2"></i>
              <span>15 min read</span>
            </div>
            <div className="flex items-center">
              <i className="ri-calendar-line mr-2"></i>
              <span>January 2025</span>
            </div>
            <div className="flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              <span>Turkey Resources</span>
            </div>
          </div>
        </div>
      </div>

      {/* Emergency Alert */}
      <div className="bg-red-50 border-l-4 border-red-500 py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start">
            <i className="ri-alarm-warning-line text-2xl text-red-500 mr-4 flex-shrink-0"></i>
            <div>
              <h3 className="text-lg font-bold text-red-900 mb-2">
                🚨 Acil Durum / Emergency
              </h3>
              <p className="text-red-800 mb-3">
                If you are in immediate danger, call:
              </p>
              <div className="space-y-2 text-red-900 font-semibold">
                <div>📞 <strong>Police:</strong> 155 (24/7)</div>
                <div>📞 <strong>Mor Çatı Kadın Sığınağı:</strong> 0 312 431 11 05 (24/7)</div>
                <div>📞 <strong>ALO 183 Sosyal Destek Hattı:</strong> 183 (24/7)</div>
                <div>📞 <strong>Kadın Acil Yardım Hattı:</strong> 183 (24/7)</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-xl text-gray-700 leading-relaxed">
            Şiddetten kaçan kadınlar için en büyük soru: "Bu gece nereye gidebilirim?" / For women fleeing violence, the biggest question is: "Where can I go tonight?"
          </p>
          <p className="text-lg text-gray-600">
            This comprehensive guide covers every emergency shelter, support service, and housing option available in Turkey. Whether you need immediate safety tonight or are planning your escape, this information can save your life.
          </p>
          <p className="text-lg text-gray-600 font-semibold">
            <strong>Important:</strong> You don't need money to leave. You don't need permission. You don't need to explain. You just need to be safe.
          </p>
        </div>

        {/* Section 1: Immediate Shelter */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white p-6 rounded-t-2xl">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-home-heart-line text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold">1. Acil Barınma / Immediate Shelter (24/7)</h2>
                <p className="text-white/90">Where to go tonight</p>
              </div>
            </div>
          </div>
          <div className="bg-red-50 p-8 rounded-b-2xl">
            
            {/* Mor Çatı */}
            <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-shield-check-line text-purple-600 mr-3 text-2xl"></i>
                Mor Çatı Kadın Sığınağı Vakfı (Purple Roof Women's Shelter)
              </h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start">
                  <i className="ri-phone-line text-purple-600 mr-3 mt-1"></i>
                  <div>
                    <strong>24/7 Hotline:</strong> 0 312 431 11 05<br/>
                    <span className="text-sm text-gray-600">Free, confidential, multilingual support</span>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-map-pin-line text-purple-600 mr-3 mt-1"></i>
                  <div>
                    <strong>Location:</strong> Ankara (main shelter)<br/>
                    <span className="text-sm text-gray-600">Confidential address provided upon contact</span>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-time-line text-purple-600 mr-3 mt-1"></i>
                  <div>
                    <strong>Duration:</strong> Up to 6 months<br/>
                    <span className="text-sm text-gray-600">Can be extended based on need</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">What They Provide:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-purple-600 mr-2 mt-1"></i>
                    <span>Safe, confidential emergency accommodation for women and children</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-purple-600 mr-2 mt-1"></i>
                    <span>Psychological counseling and trauma support</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-purple-600 mr-2 mt-1"></i>
                    <span>Legal advice and court accompaniment</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-purple-600 mr-2 mt-1"></i>
                    <span>Job training and employment assistance</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-purple-600 mr-2 mt-1"></i>
                    <span>Help finding permanent housing</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-purple-600 mr-2 mt-1"></i>
                    <span>Children's education and childcare support</span>
                  </li>
                </ul>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <strong>Cost:</strong> Completely free • <strong>Languages:</strong> Turkish, English, Kurdish, Arabic
              </div>
            </div>

            {/* Government Shelters */}
            <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-government-line text-blue-600 mr-3 text-2xl"></i>
                Şiddet Önleme ve İzleme Merkezleri (ŞÖNİM) - Violence Prevention Centers
              </h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start">
                  <i className="ri-phone-line text-blue-600 mr-3 mt-1"></i>
                  <div>
                    <strong>National Hotline:</strong> 183 (ALO Sosyal Destek Hattı)<br/>
                    <span className="text-sm text-gray-600">24/7, free from any phone</span>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-map-pin-line text-blue-600 mr-3 mt-1"></i>
                  <div>
                    <strong>Locations:</strong> 81 centers across all provinces<br/>
                    <span className="text-sm text-gray-600">Call 183 to find nearest center</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Major Cities:</h4>
                <div className="grid md:grid-cols-2 gap-4 text-gray-700">
                  <div>
                    <strong>Istanbul ŞÖNİM:</strong><br/>
                    📞 0 212 455 18 30<br/>
                    Multiple locations across city
                  </div>
                  <div>
                    <strong>Ankara ŞÖNİM:</strong><br/>
                    📞 0 312 310 60 70<br/>
                    Çankaya, Keçiören, Mamak districts
                  </div>
                  <div>
                    <strong>Izmir ŞÖNİM:</strong><br/>
                    📞 0 232 293 65 00<br/>
                    Konak, Bornova, Karşıyaka
                  </div>
                  <div>
                    <strong>Antalya ŞÖNİM:</strong><br/>
                    📞 0 242 249 60 00<br/>
                    Muratpaşa district
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Services Provided:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                    <span>Emergency shelter referrals (government-run kadın konukevleri)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                    <span>Psychological and social support</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                    <span>Legal counseling and protection orders</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                    <span>Economic support and job placement</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                    <span>Health services coordination</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Other Organizations */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Other Emergency Shelters:</h3>
              <div className="space-y-4">
                <div className="border-l-4 border-pink-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Kadın Dayanışma Vakfı (Women's Solidarity Foundation)</h4>
                  <p className="text-gray-700 text-sm mt-1">📞 0 212 256 11 00 (Istanbul)</p>
                  <p className="text-gray-600 text-sm">Emergency shelter, legal aid, psychological support</p>
                </div>
                <div className="border-l-4 border-pink-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Kadının İnsan Hakları Projesi (Women for Women's Human Rights)</h4>
                  <p className="text-gray-700 text-sm mt-1">📞 0 212 292 54 31 (Istanbul)</p>
                  <p className="text-gray-600 text-sm">Legal support, shelter referrals, advocacy</p>
                </div>
                <div className="border-l-4 border-pink-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Uçan Süpürge Kadın İletişim ve Araştırma Derneği</h4>
                  <p className="text-gray-700 text-sm mt-1">📞 0 312 430 43 05 (Ankara)</p>
                  <p className="text-gray-600 text-sm">Support services, shelter coordination, women's rights</p>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* Section 2: Insurance Support */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-6 rounded-t-2xl">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-shield-line text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold">2. Sigorta Desteği / Insurance-Supported Housing</h2>
                <p className="text-white/90">Many don't know this option exists</p>
              </div>
            </div>
          </div>
          <div className="bg-emerald-50 p-8 rounded-b-2xl">
            
            <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Konut Sigortası ile Acil Barınma / Home Insurance Emergency Housing</h3>
              <p className="text-gray-700 mb-4">
                Many home insurance policies in Turkey include <strong>temporary accommodation coverage</strong> (geçici konaklama teminatı). This can cover hotel or rental costs if your home becomes uninhabitable—and some insurers now recognize domestic violence as a valid emergency.
              </p>

              <div className="bg-emerald-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Major Turkish Insurers:</h4>
                <div className="space-y-3 text-gray-700">
                  <div className="flex items-start">
                    <i className="ri-building-line text-emerald-600 mr-3 mt-1"></i>
                    <div>
                      <strong>Anadolu Sigorta:</strong> 0 850 724 0 724<br/>
                      <span className="text-sm text-gray-600">Typically covers 30-60 days temporary housing</span>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-building-line text-emerald-600 mr-3 mt-1"></i>
                    <div>
                      <strong>Allianz Türkiye:</strong> 0 850 222 5 569<br/>
                      <span className="text-sm text-gray-600">Emergency accommodation up to policy limits</span>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-building-line text-emerald-600 mr-3 mt-1"></i>
                    <div>
                      <strong>Aksigorta:</strong> 0 850 724 0 724<br/>
                      <span className="text-sm text-gray-600">Temporary housing coverage in comprehensive policies</span>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-building-line text-emerald-600 mr-3 mt-1"></i>
                    <div>
                      <strong>Groupama Sigorta:</strong> 0 850 222 4 782<br/>
                      <span className="text-sm text-gray-600">Hotel/rental coverage for emergencies</span>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-building-line text-emerald-600 mr-3 mt-1"></i>
                    <div>
                      <strong>HDI Sigorta:</strong> 0 850 222 0 434<br/>
                      <span className="text-sm text-gray-600">Temporary accommodation in home policies</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">How to Use Your Insurance:</h4>
                <ol className="space-y-2 text-gray-700 list-decimal list-inside">
                  <li><strong>Check your policy:</strong> Look for "geçici konaklama" or "acil barınma" coverage</li>
                  <li><strong>Call your insurer's emergency line</strong> (available 24/7)</li>
                  <li><strong>Explain the situation:</strong> "Evimde şiddet nedeniyle kalamıyorum, acil barınma desteğine ihtiyacım var" (I cannot stay in my home due to violence, I need emergency accommodation support)</li>
                  <li><strong>File a police report:</strong> This strengthens your claim (but not always required)</li>
                  <li><strong>Get approval:</strong> Insurer will approve hotel or rental for covered period</li>
                  <li><strong>Keep receipts:</strong> For reimbursement if you pay upfront</li>
                </ol>
              </div>

              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">⚠️ Important Notes:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-alert-line text-red-600 mr-2 mt-1"></i>
                    <span>Not all policies include this coverage—check your policy document</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-alert-line text-red-600 mr-2 mt-1"></i>
                    <span>Coverage typically ranges from 30-90 days</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-alert-line text-red-600 mr-2 mt-1"></i>
                    <span>Daily limits usually between ₺500-2,000 per day</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-alert-line text-red-600 mr-2 mt-1"></i>
                    <span>If denied, appeal and mention Law No. 6284 (Protection of Family and Prevention of Violence Against Women)</span>
                  </li>
                </ul>
              </div>
            </div>

          </div>
        </section>

        {/* Section 3: Government Assistance */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white p-6 rounded-t-2xl">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-government-line text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold">3. Devlet Yardımları / Government Housing Assistance</h2>
                <p className="text-white/90">Financial support and social housing</p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-8 rounded-b-2xl">
            
            {/* Social Assistance */}
            <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-hand-heart-line text-blue-600 mr-3 text-2xl"></i>
                Sosyal Yardımlaşma ve Dayanışma Vakfı (SYDV) - Social Assistance Foundation
              </h3>
              <div className="space-y-3 text-gray-700 mb-4">
                <div className="flex items-start">
                  <i className="ri-phone-line text-blue-600 mr-3 mt-1"></i>
                  <div>
                    <strong>Contact:</strong> Available in every district (ilçe)<br/>
                    <span className="text-sm text-gray-600">Visit your local SYDV office or call 183 for referral</span>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Financial Support Available:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-money-dollar-circle-line text-blue-600 mr-2 mt-1"></i>
                    <span><strong>Emergency Cash Assistance:</strong> ₺2,000-5,000 one-time payment</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-money-dollar-circle-line text-blue-600 mr-2 mt-1"></i>
                    <span><strong>Monthly Support:</strong> ₺1,500-3,000/month for women fleeing violence</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-money-dollar-circle-line text-blue-600 mr-2 mt-1"></i>
                    <span><strong>Rent Assistance:</strong> Up to ₺2,000/month for housing costs</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-money-dollar-circle-line text-blue-600 mr-2 mt-1"></i>
                    <span><strong>Fuel/Heating Support:</strong> ₺1,000-2,000 for winter months</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-money-dollar-circle-line text-blue-600 mr-2 mt-1"></i>
                    <span><strong>Food Assistance:</strong> Monthly food cards (₺500-1,000)</span>
                  </li>
                </ul>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">How to Apply:</h4>
                <ol className="space-y-2 text-gray-700 list-decimal list-inside">
                  <li>Visit your local SYDV office with ID card (nüfus cüzdanı)</li>
                  <li>Explain you're fleeing domestic violence</li>
                  <li>Provide police report or ŞÖNİM referral (if available)</li>
                  <li>Fill out application form</li>
                  <li>Social worker will assess your situation</li>
                  <li>Approval typically within 1-2 weeks, emergency cases faster</li>
                </ol>
              </div>
            </div>

            {/* TOKİ Social Housing */}
            <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-building-4-line text-indigo-600 mr-3 text-2xl"></i>
                TOKİ Sosyal Konutlar (Social Housing Administration)
              </h3>
              <div className="space-y-3 text-gray-700 mb-4">
                <div className="flex items-start">
                  <i className="ri-phone-line text-indigo-600 mr-3 mt-1"></i>
                  <div>
                    <strong>Contact:</strong> 0 850 222 8 654<br/>
                    <span className="text-sm text-gray-600">Website: toki.gov.tr</span>
                  </div>
                </div>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">What TOKİ Offers:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-indigo-600 mr-2 mt-1"></i>
                    <span><strong>Affordable apartments:</strong> ₺800-2,500/month depending on city</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-indigo-600 mr-2 mt-1"></i>
                    <span><strong>Priority for violence victims:</strong> Women with protection orders get priority</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-indigo-600 mr-2 mt-1"></i>
                    <span><strong>Long-term rental:</strong> Secure housing for years</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-indigo-600 mr-2 mt-1"></i>
                    <span><strong>Purchase option:</strong> Can buy after renting for certain period</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-600">
                <strong>Note:</strong> Waiting lists can be long (6-12 months), but violence victims receive priority processing.
              </p>
            </div>

            {/* Municipality Support */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-community-line text-purple-600 mr-3 text-2xl"></i>
                Belediye Sosyal Yardım (Municipal Social Services)
              </h3>
              <p className="text-gray-700 mb-4">
                Many municipalities offer additional support beyond national programs:
              </p>
              <div className="space-y-3 text-gray-700">
                <div className="border-l-4 border-purple-500 pl-4">
                  <strong>Istanbul Metropolitan Municipality:</strong><br/>
                  📞 0 212 449 49 49<br/>
                  <span className="text-sm text-gray-600">Kadın Danışma Merkezleri (Women's Counseling Centers), rent assistance, job training</span>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <strong>Ankara Metropolitan Municipality:</strong><br/>
                  📞 0 312 595 15 55<br/>
                  <span className="text-sm text-gray-600">Emergency financial aid, housing support, legal counseling</span>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <strong>Izmir Metropolitan Municipality:</strong><br/>
                  📞 0 232 293 46 46<br/>
                  <span className="text-sm text-gray-600">Women's shelters, economic support, vocational training</span>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* Section 4: Hotels & Temporary Housing */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6 rounded-t-2xl">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-hotel-line text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold">4. Oteller ve Geçici Konaklama / Hotels & Temporary Housing</h2>
                <p className="text-white/90">Budget-friendly options while you plan next steps</p>
              </div>
            </div>
          </div>
          <div className="bg-orange-50 p-8 rounded-b-2xl">
            
            <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Budget Hotels in Major Cities:</h3>
              
              <div className="space-y-6">
                {/* Istanbul */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-map-pin-line text-orange-600 mr-2"></i>
                    Istanbul
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="border-l-4 border-orange-500 pl-4">
                      <strong>Ibis Istanbul Esenyurt</strong><br/>
                      <span className="text-sm text-gray-600">From ₺800/night</span>
                    </div>
                    <div className="border-l-4 border-orange-500 pl-4">
                      <strong>Hotel Büyük Keban</strong><br/>
                      <span className="text-sm text-gray-600">From ₺600/night</span>
                    </div>
                    <div className="border-l-4 border-orange-500 pl-4">
                      <strong>Cordial House Hotel</strong><br/>
                      <span className="text-sm text-gray-600">From ₺700/night</span>
                    </div>
                    <div className="border-l-4 border-orange-500 pl-4">
                      <strong>Hostel options</strong><br/>
                      <span className="text-sm text-gray-600">From ₺300/night (shared room)</span>
                    </div>
                  </div>
                </div>

                {/* Ankara */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-map-pin-line text-orange-600 mr-2"></i>
                    Ankara
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="border-l-4 border-orange-500 pl-4">
                      <strong>Otel Mithat</strong><br/>
                      <span className="text-sm text-gray-600">From ₺500/night</span>
                    </div>
                    <div className="border-l-4 border-orange-500 pl-4">
                      <strong>Hotel Ickale</strong><br/>
                      <span className="text-sm text-gray-600">From ₺650/night</span>
                    </div>
                    <div className="border-l-4 border-orange-500 pl-4">
                      <strong>Deeps Hostel Ankara</strong><br/>
                      <span className="text-sm text-gray-600">From ₺250/night</span>
                    </div>
                  </div>
                </div>

                {/* Izmir */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-map-pin-line text-orange-600 mr-2"></i>
                    Izmir
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="border-l-4 border-orange-500 pl-4">
                      <strong>Ibis Izmir Alsancak</strong><br/>
                      <span className="text-sm text-gray-600">From ₺750/night</span>
                    </div>
                    <div className="border-l-4 border-orange-500 pl-4">
                      <strong>Otel Kilim</strong><br/>
                      <span className="text-sm text-gray-600">From ₺550/night</span>
                    </div>
                    <div className="border-l-4 border-orange-500 pl-4">
                      <strong>Konak Hostel</strong><br/>
                      <span className="text-sm text-gray-600">From ₺300/night</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">💡 Money-Saving Tips:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-lightbulb-line text-yellow-600 mr-2 mt-1"></i>
                    <span><strong>Ask for weekly rates:</strong> Usually 20-30% cheaper than daily rates</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-lightbulb-line text-yellow-600 mr-2 mt-1"></i>
                    <span><strong>Mention emergency situation:</strong> Some hotels offer discounts for women in crisis</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-lightbulb-line text-yellow-600 mr-2 mt-1"></i>
                    <span><strong>SYDV may cover first few nights:</strong> Ask your social worker</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-lightbulb-line text-yellow-600 mr-2 mt-1"></i>
                    <span><strong>Consider pansiyons:</strong> Family-run guesthouses often cheaper (₺300-500/night)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-lightbulb-line text-yellow-600 mr-2 mt-1"></i>
                    <span><strong>Airbnb for longer stays:</strong> Monthly rentals can be ₺5,000-10,000/month</span>
                  </li>
                </ul>
              </div>
            </div>

          </div>
        </section>

        {/* Section 5: Long-Term Housing */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-6 rounded-t-2xl">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-home-4-line text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold">5. Uzun Vadeli Konut / Long-Term Housing Solutions</h2>
                <p className="text-white/90">Building your new life</p>
              </div>
            </div>
          </div>
          <div className="bg-teal-50 p-8 rounded-b-2xl">
            
            <div className="grid md:grid-cols-2 gap-6">
              
              {/* Social Housing */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-building-line text-teal-600 mr-2"></i>
                  TOKİ Sosyal Konutlar
                </h3>
                <p className="text-gray-700 mb-3">Government-subsidized affordable housing</p>
                <div className="space-y-2 text-sm text-gray-600">
                  <div><strong>Cost:</strong> ₺800-2,500/month</div>
                  <div><strong>Pros:</strong> Very affordable, secure, priority for violence victims</div>
                  <div><strong>Cons:</strong> Waiting list (6-12 months), limited locations</div>
                </div>
              </div>

              {/* Private Rental */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-key-line text-teal-600 mr-2"></i>
                  Özel Kiralık Daire
                </h3>
                <p className="text-gray-700 mb-3">Private rental apartments</p>
                <div className="space-y-2 text-sm text-gray-600">
                  <div><strong>Cost:</strong> ₺3,000-8,000/month (varies by city)</div>
                  <div><strong>Pros:</strong> Available immediately, more choice</div>
                  <div><strong>Cons:</strong> Requires deposit (2-3 months rent), higher cost</div>
                </div>
              </div>

              {/* Shared Housing */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-group-line text-teal-600 mr-2"></i>
                  Ortak Ev / Ev Arkadaşı
                </h3>
                <p className="text-gray-700 mb-3">Shared housing with roommates</p>
                <div className="space-y-2 text-sm text-gray-600">
                  <div><strong>Cost:</strong> ₺1,500-3,500/month per room</div>
                  <div><strong>Pros:</strong> Much cheaper, built-in support system</div>
                  <div><strong>Cons:</strong> Less privacy, need compatible roommates</div>
                </div>
              </div>

              {/* Women's Cooperatives */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-community-line text-teal-600 mr-2"></i>
                  Kadın Kooperatifleri
                </h3>
                <p className="text-gray-700 mb-3">Women's housing cooperatives</p>
                <div className="space-y-2 text-sm text-gray-600">
                  <div><strong>Cost:</strong> ₺2,000-4,000/month</div>
                  <div><strong>Pros:</strong> Supportive community, affordable, safe</div>
                  <div><strong>Cons:</strong> Limited availability, may have waiting list</div>
                </div>
              </div>

            </div>

            <div className="mt-6 bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Finding Rental Housing:</h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start">
                  <i className="ri-global-line text-teal-600 mr-3 mt-1"></i>
                  <div>
                    <strong>Online Platforms:</strong><br/>
                    <span className="text-sm text-gray-600">sahibinden.com, hurriyetemlak.com, zingat.com, emlakjet.com</span>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-phone-line text-teal-600 mr-3 mt-1"></i>
                  <div>
                    <strong>Real Estate Agents:</strong><br/>
                    <span className="text-sm text-gray-600">Can help find housing quickly (usually charge 1 month rent as commission)</span>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-group-line text-teal-600 mr-3 mt-1"></i>
                  <div>
                    <strong>Women's Organizations:</strong><br/>
                    <span className="text-sm text-gray-600">Mor Çatı and other shelters often have housing referral networks</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* Section 6: Additional Support */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6 rounded-t-2xl">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-heart-line text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold">6. Ek Destek Hizmetleri / Additional Support Services</h2>
                <p className="text-white/90">You're not alone</p>
              </div>
            </div>
          </div>
          <div className="bg-purple-50 p-8 rounded-b-2xl">
            
            <div className="grid md:grid-cols-2 gap-6">
              
              {/* Legal Support */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-scales-line text-purple-600 mr-2"></i>
                  Hukuki Destek / Legal Support
                </h3>
                <div className="space-y-3 text-gray-700 text-sm">
                  <div>
                    <strong>Türkiye Barolar Birliği (Turkish Bar Association)</strong><br/>
                    📞 0 312 425 30 00<br/>
                    Free legal consultation for violence victims
                  </div>
                  <div>
                    <strong>Adli Yardım Büroları (Legal Aid Offices)</strong><br/>
                    Available in every courthouse<br/>
                    Free lawyer for court cases
                  </div>
                  <div>
                    <strong>Law No. 6284 Protection Orders:</strong><br/>
                    Restraining orders, eviction of abuser, custody protection
                  </div>
                </div>
              </div>

              {/* Psychological Support */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-mental-health-line text-pink-600 mr-2"></i>
                  Psikolojik Destek / Psychological Support
                </h3>
                <div className="space-y-3 text-gray-700 text-sm">
                  <div>
                    <strong>ALO 183 Sosyal Destek Hattı</strong><br/>
                    📞 183 (24/7, free)<br/>
                    Crisis counseling and referrals
                  </div>
                  <div>
                    <strong>Türk Psikologlar Derneği</strong><br/>
                    📞 0 312 466 52 76<br/>
                    Referrals to trauma therapists
                  </div>
                  <div>
                    <strong>Community Mental Health Centers (TRSM):</strong><br/>
                    Free therapy in every province
                  </div>
                </div>
              </div>

              {/* Employment Support */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-briefcase-line text-indigo-600 mr-2"></i>
                  İş Bulma Desteği / Employment Support
                </h3>
                <div className="space-y-3 text-gray-700 text-sm">
                  <div>
                    <strong>İŞKUR (Turkish Employment Agency)</strong><br/>
                    📞 0 850 744 0 000<br/>
                    Job placement, vocational training, unemployment benefits
                  </div>
                  <div>
                    <strong>Kadın Emeğini Değerlendirme Vakfı (KEDV)</strong><br/>
                    📞 0 212 292 66 31<br/>
                    Job training for women
                  </div>
                  <div>
                    <strong>Priority hiring programs:</strong><br/>
                    Some companies prioritize hiring violence survivors
                  </div>
                </div>
              </div>

              {/* Children's Support */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-parent-line text-rose-600 mr-2"></i>
                  Çocuk Desteği / Children's Support
                </h3>
                <div className="space-y-3 text-gray-700 text-sm">
                  <div>
                    <strong>ALO 183 Çocuk Destek Hattı</strong><br/>
                    📞 183<br/>
                    Support for children who witnessed violence
                  </div>
                  <div>
                    <strong>School enrollment assistance:</strong><br/>
                    ŞÖNİM helps transfer children to new schools
                  </div>
                  <div>
                    <strong>Childcare support:</strong><br/>
                    Some municipalities offer free daycare for working mothers
                  </div>
                </div>
              </div>

            </div>

          </div>
        </section>

        {/* Section 7: Action Plan */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-rose-500 to-red-500 text-white p-6 rounded-t-2xl">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-roadmap-line text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold">7. Adım Adım Eylem Planı / Step-by-Step Action Plan</h2>
                <p className="text-white/90">Your roadmap to safety and independence</p>
              </div>
            </div>
          </div>
          <div className="bg-rose-50 p-8 rounded-b-2xl">
            
            <div className="space-y-6">
              
              {/* Day 1 */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="bg-red-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">1</span>
                  Gün 1: Güvenliğe Ulaşın / Day 1: Get to Safety
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-red-500 mr-2 mt-1"></i>
                    <span>Call 155 (police) if in immediate danger</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-red-500 mr-2 mt-1"></i>
                    <span>Call Mor Çatı (0 312 431 11 05) or ALO 183 for emergency shelter</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-red-500 mr-2 mt-1"></i>
                    <span>If you have insurance, call emergency line for hotel coverage</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-red-500 mr-2 mt-1"></i>
                    <span>Take essential documents (ID, children's documents, bank cards)</span>
                  </li>
                </ul>
              </div>

              {/* Week 1 */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="bg-orange-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">2</span>
                  Hafta 1: İstikrar Sağlayın / Week 1: Stabilize
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-orange-500 mr-2 mt-1"></i>
                    <span>File police report (suç duyurusu) for documentation</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-orange-500 mr-2 mt-1"></i>
                    <span>Apply for Law 6284 protection order at courthouse</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-orange-500 mr-2 mt-1"></i>
                    <span>Visit local SYDV office for emergency financial assistance</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-orange-500 mr-2 mt-1"></i>
                    <span>Contact ŞÖNİM for comprehensive support services</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-orange-500 mr-2 mt-1"></i>
                    <span>Get free legal consultation from Bar Association</span>
                  </li>
                </ul>
              </div>

              {/* Weeks 2-4 */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="bg-yellow-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">3</span>
                  Hafta 2-4: Sonraki Adımları Planlayın / Weeks 2-4: Plan Next Steps
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-yellow-600 mr-2 mt-1"></i>
                    <span>Apply for TOKİ social housing (priority processing)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-yellow-600 mr-2 mt-1"></i>
                    <span>Start looking at private rentals on sahibinden.com</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-yellow-600 mr-2 mt-1"></i>
                    <span>Register with İŞKUR for job placement and training</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-yellow-600 mr-2 mt-1"></i>
                    <span>Enroll children in new school (ŞÖNİM can help)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-yellow-600 mr-2 mt-1"></i>
                    <span>Start therapy/counseling for yourself and children</span>
                  </li>
                </ul>
              </div>

              {/* Months 2-3 */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="bg-green-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">4</span>
                  Ay 2-3: Kalıcı Konuta Geçin / Months 2-3: Move to Permanent Housing
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-green-600 mr-2 mt-1"></i>
                    <span>Accept TOKİ housing offer or sign private rental lease</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-green-600 mr-2 mt-1"></i>
                    <span>Use SYDV rent assistance for deposit and first month</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-green-600 mr-2 mt-1"></i>
                    <span>Start job or vocational training program</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-green-600 mr-2 mt-1"></i>
                    <span>Build support network (friends, women's groups, community)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-green-600 mr-2 mt-1"></i>
                    <span>Continue therapy and legal proceedings</span>
                  </li>
                </ul>
              </div>

            </div>

          </div>
        </section>

        {/* Emergency Contacts Summary */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white p-8 rounded-2xl shadow-xl">
            <h2 className="text-2xl font-bold mb-6 flex items-center">
              <i className="ri-phone-line mr-3 text-3xl"></i>
              Acil Durum İletişim Özeti / Emergency Contacts Summary
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-3 text-lg">🚨 Immediate Emergency:</h3>
                <div className="space-y-2 text-white/95">
                  <div>📞 <strong>Police:</strong> 155</div>
                  <div>📞 <strong>Ambulance:</strong> 112</div>
                  <div>📞 <strong>Mor Çatı 24/7:</strong> 0 312 431 11 05</div>
                  <div>📞 <strong>ALO 183:</strong> 183</div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-3 text-lg">🏠 Housing & Support:</h3>
                <div className="space-y-2 text-white/95">
                  <div>📞 <strong>ŞÖNİM:</strong> 183</div>
                  <div>📞 <strong>SYDV:</strong> Visit local office</div>
                  <div>📞 <strong>TOKİ:</strong> 0 850 222 8 654</div>
                  <div>📞 <strong>İŞKUR:</strong> 0 850 744 0 000</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Important Reminders */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-8 rounded-2xl border-2 border-purple-300">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-heart-line text-purple-600 mr-3 text-3xl"></i>
              Önemli Hatırlatmalar / Important Reminders
            </h2>
            <div className="space-y-4 text-gray-800">
              <div className="flex items-start">
                <i className="ri-checkbox-circle-fill text-purple-600 mr-3 mt-1 text-xl"></i>
                <p><strong>You don't need money to leave.</strong> Emergency shelters are free, and financial assistance is available.</p>
              </div>
              <div className="flex items-start">
                <i className="ri-checkbox-circle-fill text-purple-600 mr-3 mt-1 text-xl"></i>
                <p><strong>You don't need permission.</strong> You have the legal right to leave and seek safety.</p>
              </div>
              <div className="flex items-start">
                <i className="ri-checkbox-circle-fill text-purple-600 mr-3 mt-1 text-xl"></i>
                <p><strong>Your children will be safe.</strong> Shelters accommodate children and provide education support.</p>
              </div>
              <div className="flex items-start">
                <i className="ri-checkbox-circle-fill text-purple-600 mr-3 mt-1 text-xl"></i>
                <p><strong>You don't have to explain.</strong> Support services are confidential and non-judgmental.</p>
              </div>
              <div className="flex items-start">
                <i className="ri-checkbox-circle-fill text-purple-600 mr-3 mt-1 text-xl"></i>
                <p><strong>Help is available 24/7.</strong> You can call emergency lines any time, day or night.</p>
              </div>
              <div className="flex items-start">
                <i className="ri-checkbox-circle-fill text-purple-600 mr-3 mt-1 text-xl"></i>
                <p><strong>You deserve safety and peace.</strong> Violence is never your fault, and you deserve a life free from fear.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="mb-12">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8 rounded-2xl text-center">
            <h2 className="text-3xl font-bold mb-4">You Are Not Alone</h2>
            <p className="text-xl mb-6 text-white/90">
              Thousands of women in Turkey have successfully left violent situations and rebuilt their lives. You can too.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/safety"
                className="bg-white text-purple-600 px-8 py-3 rounded-full font-semibold hover:bg-purple-50 transition-colors whitespace-nowrap inline-flex items-center"
              >
                <i className="ri-shield-check-line mr-2"></i>
                Safety Tools
              </Link>
              <Link
                to="/demands"
                className="bg-purple-800 text-white px-8 py-3 rounded-full font-semibold hover:bg-purple-900 transition-colors whitespace-nowrap inline-flex items-center"
              >
                <i className="ri-megaphone-line mr-2"></i>
                Sign Our Petition
              </Link>
              <Link
                to="/donate"
                className="bg-pink-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-pink-600 transition-colors whitespace-nowrap inline-flex items-center"
              >
                <i className="ri-heart-line mr-2"></i>
                Support Our Work
              </Link>
            </div>
          </div>
        </section>

        {/* Share Section */}
        <section className="border-t border-gray-200 pt-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Share This Resource</h3>
              <p className="text-gray-600">Help other women find the support they need</p>
            </div>
            <div className="flex gap-3">
              <button className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors">
                <i className="ri-facebook-fill"></i>
              </button>
              <button className="w-10 h-10 bg-sky-500 text-white rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors">
                <i className="ri-twitter-fill"></i>
              </button>
              <button className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center hover:bg-green-700 transition-colors">
                <i className="ri-whatsapp-fill"></i>
              </button>
              <button className="w-10 h-10 bg-gray-600 text-white rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors">
                <i className="ri-mail-fill"></i>
              </button>
            </div>
          </div>
        </section>

      </article>
    </div>
  );
}
